/**
 * The one cache of neuroimaging volume inputs (FE Arch 1.0 Step 2, #1166).
 *
 * MRI payloads (patient workspace), uploaded neuroimages (#620) and the analysis
 * Medical Viewer's payload (keyed by analysis AND assigned MRI, #822) used to live
 * in three module-level maps that grew for the page's lifetime, kept a failed load
 * until reload, and were invalidated by hand from every mutation site in
 * `manage/` (#682, #822, #183). Here:
 *
 * - concurrent requests for one key share a load, which still goes through the
 *   volume-load queue (one server-side decode at a time, #717);
 * - a load that fails is forgotten, so the next request retries;
 * - resolved payloads are kept least-recently-used first under a byte budget —
 *   the entry just loaded and loads in flight are never evicted, and a single
 *   payload over budget is still served;
 * - the API mutations that change a volume invalidate it themselves (`api/mri.ts`,
 *   `api/recording.ts`, `api/edf.ts`, `api/neuroimage.ts`), so no component does.
 *
 * Each tab has its own store: another tab showing the same MRI still needs a reload.
 *
 * `volumeStore` is a module-level singleton by design — the API modules, which are
 * plain functions, must reach it — and `global_setup.ts` clears it before each test.
 */

import { Observable, from } from 'rxjs';
import type { Brain3dInputs } from '@brainfocus/web/domain/neuroimage/brain_3d';
import { enqueueVolumeLoad } from '@brainfocus/web/volume_load_queue';

/** Budget for resolved payloads: room for a few patients' MRI + CT + overlays. */
export const VOLUME_STORE_BUDGET_BYTES = 1.5 * 1024 ** 3;

export const mriVolumeKey = (mriId: number): string => `mri:${mriId}`;
export const neuroimageVolumeKey = (fileId: number): string => `neuroimage:${fileId}`;
export const analysisVolumeKey = (analysisId: number, mriRecordId?: number | null): string =>
    `analysis:${analysisId}:${mriRecordId ?? 'none'}`;

type Loaded = Brain3dInputs | null;

interface Entry {
    promise: Promise<Loaded>;
    /** Bytes once resolved; `null` while in flight. */
    size: number | null;
}

function bytesOf(buffer: ArrayBufferView | ArrayBuffer | null | undefined): number {
    return buffer?.byteLength ?? 0;
}

/** Memory a payload holds: every volume and mesh buffer in it. */
export function brain3dByteSize(inputs: Loaded): number {
    if (!inputs) return 0;
    let total = bytesOf(inputs.mri) + bytesOf(inputs.stlData) + bytesOf(inputs.segRgba) + bytesOf(inputs.segLabels?.labels);
    for (const overlay of Object.values(inputs.overlays ?? {})) total += bytesOf(overlay);
    return total;
}

export interface VolumeStoreOptions {
    budgetBytes?: number;
    enqueue?: <T>(task: () => Promise<T>) => Promise<T>;
}

export class VolumeStore {

    private readonly entries = new Map<string, Entry>();
    private readonly budgetBytes: number;
    private readonly enqueue: <T>(task: () => Promise<T>) => Promise<T>;

    constructor(options: VolumeStoreOptions = {}) {
        this.budgetBytes = options.budgetBytes ?? VOLUME_STORE_BUDGET_BYTES;
        this.enqueue = options.enqueue ?? enqueueVolumeLoad;
    }

    /** Bytes held by resolved payloads. */
    public get bytes(): number {
        let total = 0;
        for (const entry of this.entries.values()) total += entry.size ?? 0;
        return total;
    }

    public has(key: string): boolean {
        return this.entries.has(key);
    }

    /** The payload for `key`, loading it with `loader` on a miss. */
    public load(key: string, loader: () => Promise<Loaded>): Observable<Loaded> {
        const hit = this.entries.get(key);
        if (hit) {
            // Most recently used goes last.
            this.entries.delete(key);
            this.entries.set(key, hit);
            return from(hit.promise);
        }
        const entry: Entry = { promise: this.enqueue(loader), size: null };
        this.entries.set(key, entry);
        entry.promise.then(
            (value) => {
                if (this.entries.get(key) !== entry) return; // invalidated while in flight
                entry.size = brain3dByteSize(value);
                this.evict(key);
            },
            () => {
                if (this.entries.get(key) === entry) this.entries.delete(key);
            },
        );
        return from(entry.promise);
    }

    /** An MRI changed (segmentation, CT/MRI overlay, deletion): drop it and every analysis payload. */
    public invalidateMri(mriId: number): void {
        this.entries.delete(mriVolumeKey(mriId));
        // Analysis payloads are keyed by analysis; which ones show this MRI cannot be
        // known here, so all go (#822). A refetch of what is on screen at worst.
        this.dropWhere(key => key.startsWith('analysis:'));
    }

    /** The patient's EDFs changed: every MRI payload's consistency report is stale (#183). */
    public invalidateAllMri(): void {
        this.dropWhere(key => key.startsWith('mri:'));
    }

    public invalidateNeuroimage(fileId: number): void {
        this.entries.delete(neuroimageVolumeKey(fileId));
    }

    /** Every scan cached under one analysis. */
    public invalidateAnalysis(analysisId: number): void {
        this.dropWhere(key => key.startsWith(`analysis:${analysisId}:`));
    }

    public clear(): void {
        this.entries.clear();
    }

    private dropWhere(match: (key: string) => boolean): void {
        for (const key of [...this.entries.keys()]) {
            if (match(key)) this.entries.delete(key);
        }
    }

    private evict(justLoaded: string): void {
        let total = this.bytes;
        for (const [key, entry] of [...this.entries]) {
            if (total <= this.budgetBytes) return;
            if (key === justLoaded || entry.size === null) continue;
            this.entries.delete(key);
            total -= entry.size;
        }
    }

}

/** The app's volume store. */
export const volumeStore = new VolumeStore();
